import { saveSiteForDap, saveSiteForMyProfile } from './axiosCDPFetcher';

const PENDING_SAVE_SITE_KEY = 'pendingSaveSite';

type PendingSaveSite = {
  siteId: string;
  pageLink: string;
};

export const getPendingSaveSite = (): PendingSaveSite | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const value = sessionStorage.getItem(PENDING_SAVE_SITE_KEY);
  if (!value) {
    return null;
  }

  try {
    const pendingSaveSite = JSON.parse(value) as PendingSaveSite;
    return pendingSaveSite?.siteId ? pendingSaveSite : null;
  } catch (_error) {
    sessionStorage.removeItem(PENDING_SAVE_SITE_KEY);
    return null;
  }
};

export const setPendingSaveSite = (siteId: string, pageLink: string): void => {
  if (typeof window === 'undefined' || !siteId) {
    return;
  }

  sessionStorage.setItem(PENDING_SAVE_SITE_KEY, JSON.stringify({ siteId, pageLink }));
};

export const clearPendingSaveSite = (): void => {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem(PENDING_SAVE_SITE_KEY);
  }
};

export const getPreferredSiteIdForRegistration = (): string | null => {
  return getPendingSaveSite()?.siteId || null;
};

/** CDP AddOrUpdateCrRegistrationDetails expects preferredSites as a string array. */
export const buildPreferredSitesForCdp = (siteId: string): string[] => [siteId];

/** DAP AddOrUpdateCRRegDetails expects preferredSites as a key/value collection. */
export const buildPreferredSitesForDap = (siteId: string) => ({
  items: [{ id: siteId, text: siteId, sitecoreId: '' }],
  lastUpdated: new Date(),
});

export type SaveSiteModalContent = {
  modalTitle: string;
  modalDescription: string;
  inputText: string;
  emailSentTitle: string;
  emailSentDescription: string;
};

export const SAVE_SITE_MODAL_CONTENT: SaveSiteModalContent = {
  modalTitle: 'Save site',
  modalDescription:
    'Please provide your email address, if your account exists, we will send you a link to add site and manage your profile.',
  inputText: 'What is your email?',
  emailSentTitle: 'Check Your Email',
  emailSentDescription:
    "<p>If your account exists, we've sent a link to your email at <strong>[email]</strong>. " +
    'Sometimes email programs put messages from us in folders other than your inbox. ' +
    'Please be sure to check your other email folders.</p>' +
    '<p>Use the link provided in the email to access your profile. Please click on the link ' +
    'provided in the email to access your profile and manage your saved sites.</p>',
};

/**
 * Overlays the Save Site copy onto the shared My Profile modal fields so the same modal
 * can be reused, keeping the Sitecore-authored CTA buttons intact.
 */
export const withSaveSiteModalContent = (
  profileFields: any,
  content?: Partial<SaveSiteModalContent>
) => {
  const copy = { ...SAVE_SITE_MODAL_CONTENT, ...content };

  return {
    ...profileFields,
    myProfile: {
      ...profileFields?.myProfile,
      fields: {
        ...profileFields?.myProfile?.fields,
        modalTitle: { value: copy.modalTitle },
        modalDescription: { value: copy.modalDescription },
        inputText: { value: copy.inputText },
      },
    },
    emailSent: {
      ...profileFields?.emailSent,
      fields: {
        ...profileFields?.emailSent?.fields,
        modalTitle: { value: copy.emailSentTitle },
        modalDescription: { value: copy.emailSentDescription },
      },
    },
  };
};

export const getPreferredSiteTokenFromQuery = (
  query: Record<string, string | string[] | undefined>
): string | undefined => {
  const token = query.preferredSitesToken || query.preferredSiteToken || query.siteToken;

  return typeof token === 'string' ? token : undefined;
};

export const savePreferredSite = async (token: string, siteId: string): Promise<void> => {
  if (!siteId) {
    return;
  }

  const payload = {
    myProfileToken: token,
    siteId,
    isSaved: true,
  };

  const savePromises: Promise<unknown>[] = [saveSiteForMyProfile(payload)];

  if (process.env.NEXT_PUBLIC_ALLOW_REGISTRATION_API_CALL === 'true') {
    savePromises.push(saveSiteForDap(payload));
  }

  await Promise.all(savePromises);
};
